import {
  authenticateWithBiometrics,
  checkBiometricAvailability,
  getBiometryTypeName
} from '@shared/services/biometrics-auth.service';
import {
  clearBiometricData,
  isBiometricEnabled,
  setBiometricEnabled
} from '@shared/services/biometrics-storage.service';
import { storageService } from '@shared/services/storage.service';
import { AuthActions } from '@shared/store/auth/auth';
import { selectAuth } from '@shared/store/auth/auth.selectors';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const useBiometricAuth = () => {
  const dispatch = useDispatch();
  const {
    isBiometricAvailable,
    isBiometricEnabled: isBiometricEnabledInState,
    biometricType
  } = useSelector(selectAuth);

  // Initialize biometric availability and preferences on mount
  const initializeBiometrics = useCallback(async () => {
    try {
      const availability = await checkBiometricAvailability();
      const enabled = await isBiometricEnabled();

      dispatch(
        AuthActions.setBiometricState({
          available: availability.isAvailable,
          enabled: enabled && availability.isAvailable,
          type: getBiometryTypeName(availability?.biometryType)
        })
      );
    } catch (error) {
      console.error('[BiometricAuth] Initialization failed:', error);
    }
  }, [dispatch]);

  // Setup biometric authentication after successful OAuth login
  const enableBiometrics = useCallback(async (): Promise<boolean> => {
    // Initialize biometrics first (in case it wasn't done yet)
    await initializeBiometrics();

    // Get fresh availability after initialization
    const availability = await checkBiometricAvailability();
    if (!availability.isAvailable) throw new Error('Biometrics not available on this device');

    // Test biometric authentication
    const authResult = await authenticateWithBiometrics('Set up biometric login for faster access');
    if (!authResult.success) throw new Error('User rejected biometric authentication');

    try {
      await setBiometricEnabled(true);

      // Update Redux state
      dispatch(
        AuthActions.setBiometricState({
          available: true,
          enabled: true,
          type: getBiometryTypeName(availability?.biometryType)
        })
      );

      return true;
    } catch (error) {
      console.error('[BiometricAuth] Setup failed:', error);
      throw error;
    }
  }, [dispatch, initializeBiometrics]);

  // Disable biometric authentication
  const disableBiometrics = useCallback(async () => {
    await clearBiometricData();
    await storageService.clearTokens();
    await storageService.clearUserData();

    dispatch(AuthActions.setBiometricState({ available: !!isBiometricAvailable, enabled: false, type: biometricType }));
  }, [isBiometricAvailable, biometricType, dispatch]);

  useEffect(() => {
    initializeBiometrics();
  }, [initializeBiometrics]);

  return { isBiometricAvailable, isBiometricEnabledInState, biometricType, enableBiometrics, disableBiometrics };
};
